import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Container,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSessionStorage } from "usehooks-ts";


const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [totalOrders, setTotalOrders] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, [currentPage, ordersPerPage, statusFilter, search]);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const params = {
        page: currentPage,
        per_page: ordersPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search || undefined
      };
      const API_BASE = process.env.REACT_APP_API_BASE_URL;

      const res = await axios.get(`${API_BASE}/api/orders_a/enriched`, { 
        headers,
        params
      });
  
      setOrders(res.data.orders);
      setTotalOrders(res.data.total);
    } catch (err) {
      console.error("Failed to fetch orders", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTracking = (order_id, customer_id) => {
   navigate(`/admin/editTracking/${order_id}/${customer_id}`);

  };

  const handleAddPayment = (order) => {
    const grandTotal = order.items.reduce(
      (sum, item) => sum + item.quantity * item.unit_price,
      0
    );
    navigate(`/admin/payment/${order.order_id}`, {
      state: { grandTotal },
    });
  };

  const handleView = (order_id) => {
    navigate(`/admin/viewOrder/${order_id}`);
  };

  const summarizeItems = (items = []) => (
    <ul style={{ marginBottom: 0 }}>
      {items.map((item, index) => (
        <li key={index}>
          {item.quantity}x {item.bread_type} ({item.size}")
        </li>
      ))}
    </ul>
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePerPageChange = (e) => {
    const value = e.target.value === "all" ? 9999 : parseInt(e.target.value);
    setOrdersPerPage(value);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  return (
    <Container className="mt-4">
      <h2 className="mb-4">My Orders</h2>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by company or tracking code"
            value={search}
            onChange={handleSearchChange}
          />
        </Col>
        <Col md={3}>
          <Form.Select
            value={statusFilter}
            onChange={handleStatusChange}
          >
            <option value="all">All Status</option>
            <option value="processing">Processing</option>
            <option value="delivered">Delivered</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={ordersPerPage === 9999 ? "all" : ordersPerPage}
            onChange={handlePerPageChange}
          >
            <option value="10">Show 10</option>
            <option value="20">Show 20</option>
            <option value="50">Show 50</option>
            <option value="all">Show All</option>
          </Form.Select>
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center my-4">Loading orders...</div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Order ID</th>
                <th>Company</th>
                <th>Date</th>
                <th>Items</th>
                <th>Tracking</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, index) => (
                <tr key={order.order_id}>
                  <td>{(currentPage - 1) * ordersPerPage + index + 1}</td>
                  <td>{order.order_id}</td>
                  <td>{order.company_name}</td>
                  <td>{new Date(order.order_date).toLocaleDateString()}</td>
                  <td>{summarizeItems(order.items)}</td>
                  <td>{order.tracking_code || "N/A"}</td>
                  <td>
                    <Button
                      variant="info"
                      size="sm"
                      className="me-1"
                      onClick={() => handleView(order.order_id)}
                    >
                      View
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-1"
                      onClick={() => handleUpdateTracking(order.order_id, order.customer_id)}
                    >
                      Update Tracking
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleAddPayment(order)}
                    >
                      Update Payment
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {ordersPerPage !== 9999 && totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Button
                variant="outline-secondary"
                className="mx-1"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                First
              </Button>
              <Button
                variant="outline-secondary"
                className="mx-1"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              
              {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === currentPage ? "primary" : "outline-secondary"}
                    className="mx-1"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              <Button
                variant="outline-secondary"
                className="mx-1"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
              <Button
                variant="outline-secondary"
                className="mx-1"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last
              </Button>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default OrderList;